import React, { useState, useEffect } from 'react';
import {
  Button, Typography, Box, TextField, CircularProgress, Alert, MenuItem, Select, InputLabel, FormControl,
  Link
} from '@mui/material';
import axios from 'axios';
import { checkParticipantExists, getParticipantById2, fetchAgeGroups, AgeGroup, API_BASE_URL } from '../api/contests';

const TG_COMPARATOR_BASE_URL = 'https://photoskiff.com/zaruba/zone-comparator-2.6.0-fix.html';
const COMPARATOR_ALLOWED_PARTICIPANT_IDS = new Set([55, 24, 49, 11, 80, 20, 72, 42, 84, 1, 56, 71, 58, 70, 61, 12]);

type TelegramUser = {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  allows_write_to_pm?: boolean;
};

type ComparatorPreset = {
  href: string;
  title: string;
};

const encodeSchemaBaseForUrlParam = (schemaBase: string): string => {
  // Keep the URL human-readable and avoid WAF rules that can trigger on
  // heavily percent-encoded query strings.
  //
  // We still encode non-ASCII + spaces safely, but we preserve the most common
  // Zaruba DSL characters.
  return encodeURIComponent(schemaBase)
    .replace(/%2F/gi, '/')
    .replace(/%40/gi, '@')
    .replace(/%2A/gi, '*')
    .replace(/%28/gi, '(')
    .replace(/%29/gi, ')')
    .replace(/%2B/gi, '+');
};

const splitSchemeBaseAndTail = (schemeLine: string): { base: string; tail: string } => {
  const hashIndex = schemeLine.indexOf('#');
  if (hashIndex < 0) return { base: schemeLine, tail: '' };
  return {
    base: schemeLine.slice(0, hashIndex),
    tail: schemeLine.slice(hashIndex + 1)
  };
};

const encodeTailForFragment = (tail: string): string => {
  // Fragment never hits the server, but it still must be URL-safe in chats.
  return encodeURIComponent(tail);
};

const buildOneClickTimerHref = (schemeLine: string): string => {
  const trimmed = schemeLine.trim();
  const { base, tail } = splitSchemeBaseAndTail(trimmed);

  // IMPORTANT:
  // - Putting "#title=..." into the `schema=` query param requires %23 encoding,
  //   which can be blocked by some WAF/proxy setups on production.
  // - Putting the tail into the URL fragment avoids hitting the server, and
  //   zone-comparator stitches it back into schema on load.
  const schemaBaseEncoded = encodeSchemaBaseForUrlParam(base);
  const hashPart = tail ? ('#' + encodeTailForFragment(tail)) : '';
  return `${TG_COMPARATOR_BASE_URL}?timer=1&schema=${schemaBaseEncoded}${hashPart}`;
};

const decodeUrlish = (s: string): string => {
  // Handles occasional cases where the tail is already URL-encoded.
  // (e.g. spaces as '+', percent-encoding)
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    return s;
  }
};

const extractSchemeMeta = (schemeLine: string): { title?: string } => {
  const hashIndex = schemeLine.indexOf('#');
  if (hashIndex < 0) return {};

  // Tail format is NOT querystring; it's tokens separated by spaces:
  //   #title=... note=... *comment*
  const tail = decodeUrlish(schemeLine.slice(hashIndex + 1)).trim();
  if (!tail) return {};

  let i = 0;
  const n = tail.length;
  const meta: Record<string, string> = {};
  const isWs = (ch: string) => ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';
  const isKeyChar = (ch: string) => /[A-Za-z0-9_\-]/.test(ch);

  while (i < n) {
    while (i < n && isWs(tail[i])) i++;
    if (i >= n) break;

    // Skip *comment* blocks
    if (tail[i] === '*') {
      i++;
      while (i < n && tail[i] !== '*') i++;
      if (i < n && tail[i] === '*') i++;
      continue;
    }

    // Parse key
    const keyStart = i;
    while (i < n && isKeyChar(tail[i])) i++;
    const key = tail.slice(keyStart, i);
    if (!key) {
      // Unknown token, skip to next whitespace
      while (i < n && !isWs(tail[i])) i++;
      continue;
    }
    if (i >= n || tail[i] !== '=') {
      while (i < n && !isWs(tail[i])) i++;
      continue;
    }
    i++; // skip '='

    // Parse value: can contain spaces until next " key=" or comment block
    const valStart = i;
    while (i < n) {
      if (tail[i] === '*') break;
      if (isWs(tail[i])) {
        let j = i;
        while (j < n && isWs(tail[j])) j++;

        // Check if next token looks like another key=
        let k = j;
        while (k < n && isKeyChar(tail[k])) k++;
        const looksLikeNextKey = k > j && k < n && tail[k] === '=';
        if (looksLikeNextKey) break;

        i = j;
        continue;
      }
      i++;
    }

    meta[key] = tail.slice(valStart, i).trimEnd();
  }

  return { title: meta.title };
};

const buildComparatorPresetFromSchemeLine = (schemeLine: string): ComparatorPreset => {
  // Each line is a full schema expression (can include "#title=...")
  // IMPORTANT: the whole line must go into the single query param `schema`.
  const trimmed = schemeLine.trim();

  // Extract title for rendering only (doesn't affect href).
  const title = extractSchemeMeta(trimmed).title ?? trimmed;

  return {
    href: buildOneClickTimerHref(trimmed),
    title: title || 'Схема'
  };
};


type LinkData = {
  link: string;
  description?: string;
};

type ParticipantData = {
  participant_id?: number;
  age?: number;
  age_group_id?: number;
  weight?: number;
  info?: string;
  links?: LinkData[];
  name?: string;
};


const tg = (window as any)?.Telegram?.WebApp;
const DEBUG_TG_ENABLED = (process.env.REACT_APP_TG_DEBUG ?? '') === '1';
const DEBUG_TG_USER_ID = Number(process.env.REACT_APP_TG_DEBUG_USER_ID ?? '0');

const getTelegramUser = (): TelegramUser | null => {
  try {
    const unsafeUser = tg?.initDataUnsafe?.user as TelegramUser | undefined;
    if (unsafeUser?.id) return unsafeUser;
  } catch {
    // ignore
  }

  if (DEBUG_TG_ENABLED && DEBUG_TG_USER_ID > 0) {
    return {
      id: DEBUG_TG_USER_ID,
      first_name: 'Serge',
      last_name: 'Debug',
      username: 'debug',
      language_code: 'ru',
      is_premium: true,
      allows_write_to_pm: true,
    };
  }

  return null;
};

const TelegramWebUserProfile: React.FC = () => {
  const telegramUser = getTelegramUser();
  const id = telegramUser?.id;
  const isAuthorized = !!telegramUser;
  const [userExists, setUserExists] = useState(false);
  const [participantData, setParticipantData] = useState<ParticipantData>({
    participant_id: undefined,
    age: undefined,
    age_group_id: 0,
    weight: undefined,
    info: '',
    links: [{ link: '', description: '' }]
  });
  const [loading, setLoading] = useState(true);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [submissionStatus, setSubmissionStatus] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch age groups
  useEffect(() => {
    fetchAgeGroups()
      .then(response => setAgeGroups(response))
      .catch(error => console.error('Error fetching age groups:', error));
  }, []);

  // Check if the user exists and fetch their profile data
  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setUserExists(false);
        setLoading(false);
        return;
      }

      try {
        const exists = await checkParticipantExists(id);
        setUserExists(exists);
        if (exists) {
          const data: ParticipantData = await getParticipantById2(id);
          // Handle null values
          setParticipantData({
            participant_id: data.participant_id ?? undefined,
            age: data.age ?? undefined,
            age_group_id: data.age_group_id ?? 0,
            weight: data.weight ?? undefined,
            info: data.info ?? '',
            links: data.links ?? [{ link: '', description: '' }],
            name: data.name ?? ''
          });
        }
      } catch (error) {
        console.error('Error loading participant data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  // Sanitize text input to prevent script injection
  const sanitizeInput = (input: string) => {
    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
  };

  const handleInputChange = (field: keyof ParticipantData, value: any) => {
    setParticipantData(prev => ({
      ...prev,
      [field]: value === '' ? undefined : value
    }));
  };


  const handleLinkChange = (index: number, field: keyof LinkData, value: string) => {
    setParticipantData(prev => ({
      ...prev,
      links: prev.links?.map((link, i) => i === index ? { ...link, [field]: sanitizeInput(value) } : link)
    }));
  };

  const addLinkField = () => {
    if ((participantData?.links?.length ?? 0) < 3) {
      setParticipantData(prev => ({
        ...prev,
        links: [...(prev.links ?? []), { link: '', description: '' }]
      }));
    }
  };

  const removeLinkField = (index: number) => {
    setParticipantData(prev => ({
      ...prev,
      links: prev.links?.filter((_, i) => i !== index) ?? []
    }));
  };

  const handleSubmit = async () => {
    if (!id) {
      setSubmissionStatus({ success: false, message: 'Не авторизован: откройте страницу из Telegram.' });
      return;
    }

    // Flatten the data before sending
    const formattedData = {
      age: participantData.age,
      age_group_id: participantData.age_group_id,
      weight: participantData.weight,
      info: {
        info: participantData.info || '',
        links: participantData.links || []
      }
    };

    // Convert the flattened structure to a JSON string to match the database format
    const payload = {
      age: formattedData.age,
      age_group_id: formattedData.age_group_id,
      weight: formattedData.weight,
      info: JSON.stringify({
        info: formattedData.info.info,
        links: formattedData.info.links
      })
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/update-participant/${id}`, payload);

      if (response.data.success) {
        setSubmissionStatus({ success: true, message: 'Профиль участника успешно обновлён!' });
      } else {
        setSubmissionStatus({ success: false, message: response.data.message });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSubmissionStatus({ success: false, message: 'Произошла ошибка, попробуйте ещё раз.' });
    }
  };

  const comparatorSchemeLines: string[] = [
    '60/0*60#title=бег_1_час',
    '60/0*5#title=Заруба_5_минут',
    '60/0@r20*5#title=Заруба_5_минут_темп_20',
    '60/0@t180*60#title=бег_трусцой',
    '60/0@r20*10#title=ГС_темп_20',
  ];

  const comparatorPresets: ComparatorPreset[] = [
    { href: `${TG_COMPARATOR_BASE_URL}?timer`, title: 'Таймер' },
    ...comparatorSchemeLines.map(buildComparatorPresetFromSchemeLine)
  ];
  const participantId = participantData.participant_id;
  const canSeeComparatorLinks = typeof participantId === 'number'
    && (participantId > 84 || COMPARATOR_ALLOWED_PARTICIPANT_IDS.has(participantId));

  if (loading) {
    return <CircularProgress />;
  }

  if (!isAuthorized) {
    return (
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          Не авторизован
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Откройте эту страницу из Telegram (WebApp), чтобы просматривать и редактировать профиль.
        </Alert>
      </Box>
    );
  }

  return !userExists ? (
    <Alert severity="warning" sx={{ mb: 2 }}>
      Для доступа к этой странице необходимо принять участие в зарубах.
    </Alert>
  ) : (
    <Box p={3}>
      {canSeeComparatorLinks && (
        <Box mb={2}>
          <Typography variant="body2" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Link
              href={TG_COMPARATOR_BASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 700 }}
            >
              Интерактивный компаратор зон
            </Link>

            <Link
              href={`${TG_COMPARATOR_BASE_URL}?timer`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              Заруба Таймер
            </Link>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {comparatorPresets.map(preset => (
                <Link
                  key={preset.href}
                  href={preset.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ fontWeight: 600 }}
                >
                  {preset.title}
                </Link>
              ))}
            </Box>
          </Typography>
        </Box>
      )}
      <Typography variant="h5" gutterBottom>
        Профиль участника {participantData.name}
      </Typography>
      <Typography variant="h6">
        (ни одно поле не является обязательным для заполнения)
      </Typography>
      {submissionStatus && (
        <Alert severity={submissionStatus.success ? 'success' : 'error'} sx={{ mb: 2 }}>
          {submissionStatus.message}
        </Alert>
      )}

      <TextField
        label="Возраст"
        type="number"
        variant="outlined"
        fullWidth
        margin="normal"
        value={participantData.age !== undefined ? participantData.age : ''}
        onChange={e => handleInputChange('age', e.target.value === '' ? undefined : Number(e.target.value))}
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>Возрастная группа</InputLabel>
        <Select
          value={participantData.age_group_id}
          onChange={e => handleInputChange('age_group_id', Number(e.target.value))}
          label="Возрастная Группа"
        >
          {ageGroups.map(group => (
            <MenuItem key={group.age_group_id} value={group.age_group_id}>
              {group.age_group_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Вес"
        type="number"
        variant="outlined"
        fullWidth
        margin="normal"
        value={participantData.weight !== undefined ? participantData.weight : ''}
        onChange={e => handleInputChange('weight', e.target.value === '' ? undefined : Number(e.target.value))}
      />

      <TextField
        label="О себе"
        variant="outlined"
        fullWidth
        margin="normal"
        multiline
        rows={4}
        value={sanitizeInput(participantData.info ?? '')}
        slotProps={{ htmlInput: { maxLength: 600 } }}
        helperText={`${participantData.info?.length ?? 0}/500 знаков`}
        onChange={e => handleInputChange('info', sanitizeInput(e.target.value))}
      />

      <Typography variant="h6" gutterBottom>
        Ссылки участника
      </Typography>
      {participantData.links?.map((link, index) => (
        <Box key={index} mb={2}>
          <TextField
            label="Ссылка"
            variant="outlined"
            fullWidth
            margin="normal"
            value={link.link}
            onChange={e => handleLinkChange(index, 'link', e.target.value)}
          />
          <TextField
            label="Описание"
            variant="outlined"
            fullWidth
            margin="normal"
            value={sanitizeInput(link.description ?? '')}
            onChange={e => handleLinkChange(index, 'description', e.target.value)}
          />
          {(participantData?.links?.length ?? 0) > 1 && (
            <Button onClick={() => removeLinkField(index)} color="secondary">
              Удалить ссылку
            </Button>
          )}
        </Box>
      ))}

      {(participantData?.links?.length ?? 0) < 3 && (
        <Button onClick={addLinkField} color="primary">
          Добавить ссылку
        </Button>
      )}
      {submissionStatus && (
        <Alert severity={submissionStatus.success ? 'success' : 'error'} sx={{ mb: 2 }}>
          {submissionStatus.message}
        </Alert>
      )}
      <Box mt={3}>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isAuthorized}>
          Сохранить изменения
        </Button>
      </Box>
    </Box>
  );
};

export default TelegramWebUserProfile;
